import type {
  CreateInstitutionUserPayload,
  InstitutionMember,
  ResetInstitutionUserPasswordPayload,
  SeatPreview,
  UpdateInstitutionUserPayload,
} from '../../types/index.js';
import { baseApi } from '../base-api.js';

type MembersResponse = { users: InstitutionMember[] };
type MemberMutationResponse = { member: InstitutionMember; message: string };
type CreateMemberResponse = {
  pending: boolean;
  member: InstitutionMember | null;
  client_secret: string | null;
  message: string;
};
type CreateMemberResult = Omit<CreateMemberResponse, 'message'>;
type FinalizePendingSeatResponse = { member: InstitutionMember; message: string };

// Per-item tags are keyed by the practitioner (User) id, not the join-row id: the
// mutation routes take `:user_id`, so both sides must live in the same id space.
const memberListTags = (result: InstitutionMember[] | undefined) =>
  result
    ? [
        ...result.map(({ practitioner }) => ({ type: 'InstitutionMember' as const, id: practitioner.id })),
        { type: 'InstitutionMember' as const, id: 'LIST' },
      ]
    : [{ type: 'InstitutionMember' as const, id: 'LIST' }];

/**
 * Mirrors `apps/server/src/modules/institution-member` (`institutionMemberController`, prefix
 * `/institution-members`). Cross-domain invalidations chosen by reading what each mutation
 * actually touches server-side, not by convention:
 *
 * - Adding or finalizing a seat (`createInstitutionMember`, `finalizePendingSeat`) can add a
 *   paid seat AND can silently activate a free trial for a virgin institution
 *   (`isEligibleForAutoTrial` in the controller) — either way it changes `Institution.seat_count`
 *   and possibly `subscription_status`/`trial_end_at`, which are surfaced by three different
 *   endpoints in `institution.api.ts` (`getInstitution` = SELF, `getInstitutionStats` = STATS,
 *   `getInstitutionSubscription` = SUBSCRIPTION). All three are invalidated alongside the member
 *   list.
 * - Deleting a member (`deleteInstitutionMember`) removes a seat (same three Institution tags)
 *   AND the join row in `institution_group_members` is `ON DELETE CASCADE` on `member_id` (see
 *   `1766200000000-PerSeatAndGroups.ts`), so any group that included this member silently drops
 *   them server-side — `Group` LIST is invalidated too.
 * - Updating a member (`updateInstitutionMember`) never touches seats, but `InstitutionGroup`
 *   embeds each member's live practitioner/profile (name, email) rather than a snapshot — see
 *   `institution-group.entity.ts`'s join — so a name/email change is visible through
 *   `institution-group.api.ts`'s queries too. `Group` LIST is invalidated here as well.
 * - Resetting a password (`resetInstitutionUserPassword`) changes nothing in any cached response
 *   shape (no field of `InstitutionMember` reflects the password) — no invalidation.
 * - Seat mutations do NOT invalidate `Institution` INVOICES: Stripe's `create_prorations` (used
 *   by `updateSeatCount`, without `invoice_now`) attaches the proration to the NEXT invoice, and
 *   `getInstitutionInvoices` lists finalized invoices only — nothing changes immediately.
 */
export const institutionMemberApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getInstitutionMembers: builder.query<InstitutionMember[], void>({
      query: () => ({ path: ['institution-members'], method: 'get' }),
      transformResponse: (response: MembersResponse) => response.users,
      providesTags: (result) => memberListTags(result),
    }),

    getInstitutionSeatPreview: builder.query<SeatPreview, void>({
      query: () => ({ path: ['institution-members', 'seat-preview'], method: 'get' }),
      transformResponse: (response: { preview: SeatPreview }) => response.preview,
    }),

    createInstitutionMember: builder.mutation<CreateMemberResult, CreateInstitutionUserPayload>({
      query: (body) => ({ path: ['institution-members'], method: 'post', body }),
      transformResponse: ({ pending, member, client_secret }: CreateMemberResponse) => ({
        pending,
        member,
        client_secret,
      }),
      invalidatesTags: [
        { type: 'InstitutionMember', id: 'LIST' },
        { type: 'Institution', id: 'SELF' },
        { type: 'Institution', id: 'STATS' },
        { type: 'Institution', id: 'SUBSCRIPTION' },
      ],
    }),

    finalizePendingSeat: builder.mutation<InstitutionMember, void>({
      query: () => ({ path: ['institution-members', 'finalize-pending'], method: 'post' }),
      transformResponse: (response: FinalizePendingSeatResponse) => response.member,
      invalidatesTags: [
        { type: 'InstitutionMember', id: 'LIST' },
        { type: 'Institution', id: 'SELF' },
        { type: 'Institution', id: 'STATS' },
        { type: 'Institution', id: 'SUBSCRIPTION' },
      ],
    }),

    updateInstitutionMember: builder.mutation<
      InstitutionMember,
      { userId: string; payload: UpdateInstitutionUserPayload }
    >({
      query: ({ userId, payload }) => ({
        path: ['institution-members', { param: 'user_id', value: userId }],
        method: 'put',
        body: payload,
      }),
      transformResponse: (response: MemberMutationResponse) => response.member,
      invalidatesTags: (_result, _error, { userId }) => [
        { type: 'InstitutionMember', id: userId },
        { type: 'InstitutionMember', id: 'LIST' },
        // Bare type: affected group ids are unknowable here, and cached per-id group
        // detail views project the member's live profile.
        { type: 'Group' },
      ],
    }),

    deleteInstitutionMember: builder.mutation<void, string>({
      query: (userId) => ({ path: ['institution-members', { param: 'user_id', value: userId }], method: 'delete' }),
      transformResponse: (): void => undefined,
      invalidatesTags: (_result, _error, userId) => [
        { type: 'InstitutionMember', id: userId },
        { type: 'InstitutionMember', id: 'LIST' },
        { type: 'Group' },
        { type: 'Institution', id: 'SELF' },
        { type: 'Institution', id: 'STATS' },
        { type: 'Institution', id: 'SUBSCRIPTION' },
      ],
    }),

    resetInstitutionMemberPassword: builder.mutation<
      void,
      { userId: string; payload: ResetInstitutionUserPasswordPayload }
    >({
      query: ({ userId, payload }) => ({
        path: ['institution-members', { param: 'user_id', value: userId }, 'reset-password'],
        method: 'put',
        body: payload,
      }),
      transformResponse: (): void => undefined,
    }),
  }),
});

export const {
  useGetInstitutionMembersQuery,
  useGetInstitutionSeatPreviewQuery,
  useCreateInstitutionMemberMutation,
  useFinalizePendingSeatMutation,
  useUpdateInstitutionMemberMutation,
  useDeleteInstitutionMemberMutation,
  useResetInstitutionMemberPasswordMutation,
} = institutionMemberApi;
